import { cn } from '@seen/ui'
import { type JSX, type KeyboardEvent, useContext } from 'react'
import { ObservationFrameEngagement } from '#src/components/rich-text-editor/ObservationNode/observationFrameEngagement.ts'
import {
  RichTextEditor,
  type RichTextEditorProps,
} from '#src/components/rich-text-editor/RichTextEditor.tsx'

export interface ObservationRichTextEditorProps extends RichTextEditorProps {
  // One line inside the note's line, clipped at a width; Enter opens the
  // node's details instead of starting a new line.
  singleLine?: boolean
}

export function ObservationRichTextEditor({
  className,
  singleLine = false,
  ...delegated
}: ObservationRichTextEditorProps): JSX.Element {
  const { openDetails } = useContext(ObservationFrameEngagement)

  // Taken in the capture phase, before either editor sees the key: this
  // editor must not split its line, and the note it sits in must not get the
  // Enter either. A prevented key is one neither ProseMirror view handles.
  const onKeyDownCapture = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    openDetails?.('note')
  }

  const editor = (
    <RichTextEditor
      variant="unstyled"
      className={cn(
        'rounded-[calc(var(--radius)-5px)] bg-card',
        singleLine && 'min-w-0 max-w-[18rem]',
        className
      )}
      contentClassName={cn(
        'min-h-0 px-1 py-0 leading-5 rounded-[calc(var(--radius)-5px)] pl-1 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-zinc-950 ring-offset-card bg-card ring-offset-0',
        // Important: TipTap's own stylesheet sets pre-wrap on the editor,
        // outside Tailwind's layers. The ellipsis shows only at rest; while
        // typing, the line scrolls to the caret like an input.
        singleLine &&
          'overflow-hidden whitespace-nowrap! [&_p]:truncate focus:[&_p]:text-clip'
      )}
      {...delegated}
    />
  )

  return singleLine ? (
    <div className="contents" onKeyDownCapture={onKeyDownCapture}>
      {editor}
    </div>
  ) : (
    editor
  )
}
